import { z } from 'zod'
import { lenientBoolDefault, lenientEnumDefault, lenientHex, lenientInt } from '../zod-lenient'

/**
 * THE SHARED CARD-SKIN LEAVES (#902 bullet 1, skin subset).
 *
 * Port of the toolkit's HomerunnerSkin card surface (includes/HomerunnerSkin/
 * Preset.php TOKEN_SCHEMA + FLAG_SCHEMA, group 'card'). The toolkit has no
 * preset literally named "Classic": its built-ins are `default` and
 * `editorial-square`, and default.json describes itself as "Theme-neutral
 * baseline… All flags neutral so structural HR-plugin defaults are
 * preserved." So per #870 decision 4, Classic IS the Skin base — which maps
 * exactly onto this group's contract:
 *
 *   EVERY default below is the NEUTRAL value, and buildCardSkinCss emits
 *   NOTHING for a neutral value. An unconfigured card therefore renders
 *   byte-for-byte what it rendered before this group existed.
 *
 * Sizing leaves are optional with NO default (the `lenientInt` pattern the
 * map port uses): absent means "inherit explorer.css", which is a different
 * state from any number a user could pick — so no schema default can ever
 * silently restyle a stored widget, and a widget-level save cannot
 * materialize a value that clobbers the feed-global through getFinalSettings.
 * Enum/bool leaves carry the neutral value as their default because the form
 * needs something concrete to show in the control.
 *
 * WHY IT LIVES HERE. These leaves described one widget's card until #902: the
 * explorer owned them, and `buildCardSkinCss` scoped every rule it emitted to
 * `.hfse-explorer-container`, so related-properties — which renders the SAME
 * card component (common/property-card/TemplateExplorer) — could not be skinned
 * at all. Spreading one definition into both configs is what makes the two
 * surfaces agree by construction instead of by review.
 *
 * ADDING A LEAF: it appears in every consuming widget's schema at once, so it
 * needs a surface on the card BOTH of them render, a rule in buildCardSkinCss,
 * and a uiSchema entry in EVERY consuming dashboard form (an unlisted key falls
 * into WidgetConfigSections' auto "Other" group). Keys that only make sense for
 * one consumer stay in that widget's own config — the explorer's five
 * `loadMore*` leaves are the precedent: they style the grid's Load More button,
 * which no standalone card has.
 */
export const cardSkinFields = {
  titleFontFamily: lenientEnumDefault(
    ['inherit', 'system', 'modern-sans', 'classic-sans', 'geometric', 'humanist', 'serif', 'display-serif', 'editorial', 'tenor-sans'],
    'inherit',
  ).describe('Card title font. "inherit" uses the widget font. Skin: hrtk-skin-font-family-title'),
  titleFontSize: lenientInt(12, 32).describe('Card title font size in px; empty = theme size. Skin: hrtk-skin-font-size-card-title'),
  titleTextTransform: lenientEnumDefault(['none', 'uppercase', 'lowercase', 'capitalize'], 'none')
    .describe('Card title letter case. Skin: hrtk-skin-text-transform-title'),
  subtitleFontSize: lenientInt(10, 24).describe('Card subtitle font size in px; empty = 13px. Skin: hrtk-skin-font-size-card-subtitle'),
  priceFontSize: lenientInt(10, 24).describe('Price block font size in px; empty = 14px. Skin: hrtk-skin-font-size-card-price'),
  priceColor: lenientHex().describe('Price text color (primary line); empty = inherits the card text color. Skin: hrtk-skin-color-card-price'),
  specFontSize: lenientInt(10, 24).describe('Guests/beds/baths font size in px; empty = 16px. Skin: hrtk-skin-font-size-secondary-meta'),

  radius: lenientInt(0, 50).describe('Card corner radius in px; empty = the design\'s own corners (Legacy: square, templates: 12px). Skin: hrtk-skin-radius-card'),
  slideshowRadius: lenientInt(0, 50).describe('Photo corner radius in px; empty = Legacy follows the card radius, templates keep the photo flush. Skin: hrtk-skin-radius-slideshow'),
  shadow: lenientEnumDefault(['default', 'none', 'subtle', 'card', 'pop', 'dramatic'], 'default')
    .describe('Card shadow. "default" keeps the current shadow; the other five are the Skin shadow presets and also apply on hover. Skin: hrtk-skin-shadow-card / card.show_card_shadow'),
  dotnavSize: lenientInt(4, 16).describe('Slideshow dot diameter in px; empty = 12px. Skin: hrtk-skin-card-dotnav-size'),
  imageBottomMargin: lenientInt(0, 24).describe('Gap under the photo in px; empty = none. Skin: hrtk-skin-space-card-image-mb'),

  blockOrder: lenientEnumDefault(['image-body-meta', 'image-meta-body'], 'image-body-meta')
    .describe('Card section order. Skin flag: card.block_order'),
  metaOrder: lenientEnumDefault(['location-first', 'specs-first'], 'location-first')
    .describe('Whether location/type or guests/beds/baths comes first. Skin flag: card.meta_order'),
  metaLayout: lenientEnumDefault(['row-spaced', 'stacked'], 'row-spaced')
    .describe('Location and type on one justified row, or stacked. "row-spaced" is the neutral value — the card already renders that row today. Skin flag: card.meta_layout'),
  metaSpecStyle: lenientEnumDefault(['icons', 'text'], 'icons')
    .describe('Guests/beds/baths as icon glyphs, or as text ("Sleeps 4 | 2 BR | 1 BA"). Skin flag: card.meta_spec_style'),

  priceOrder: lenientEnumDefault(['after-title', 'before-title'], 'after-title')
    .describe('Price above or below the title. Ignored when the price is positioned bottom-right. Skin flag: card.price_order'),
  pricePosition: lenientEnumDefault(['inline', 'absolute-bottom-right'], 'inline')
    .describe('Price in the normal flow, or floated over the card\'s bottom-right corner. Skin flag: card.price_position'),
  priceArrangement: lenientEnumDefault(['default', 'per-night-only', 'per-night-then-total', 'total-only'], 'default')
    .describe('Which price lines show and in what order. "default" keeps per-night · total on one line. Skin flag: card.price_arrangement'),
  showTaxLine: lenientBoolDefault(true).describe('Show the tax note under the price ("excluding taxes" on a quoted total, "excluding taxes & fees" on a calendar-derived total). Skin flag: card.show_tax_line'),

  showTopDivider: lenientBoolDefault(false).describe('Thin rule above each card. Skin flag: card.show_top_divider'),
  showSecondaryMetaDivider: lenientBoolDefault(true).describe('Rule above the guests/beds/baths row. Skin flag: card.show_secondary_meta_divider'),
  showTypeIcon: lenientBoolDefault(true).describe('Show the icon before the property-type badge. Skin flag: card.show_type_icon'),

  bodyPaddingX: lenientInt(0, 32).describe('Card body horizontal padding in px; empty = 20px. Skin: hrtk-skin-space-card-body-x'),
  bodyPaddingY: lenientInt(0, 32).describe('Card body vertical padding in px; empty = 15px. Skin: hrtk-skin-space-card-body-y'),
  metaGap: lenientInt(0, 24).describe('Gap between the location row and the specs row in px; empty = none. Skin: hrtk-skin-space-card-meta-gap'),
}

/** Every shared card-skin key, for coverage tests and uiSchema pins. */
export const CARD_SKIN_KEYS = Object.keys(cardSkinFields) as (keyof typeof cardSkinFields)[]

/**
 * The explorer-only Load More knobs. Declared in the explorer's config (they
 * style the grid's button, which no standalone card has); listed here so the
 * dashboard's live-inherit and preset scopes derive from one place instead of
 * hand-copying the card key list.
 */
export const EXPLORER_LOAD_MORE_KEYS = ['loadMoreBg', 'loadMoreText', 'loadMoreHover', 'loadMoreRadius', 'loadMoreAlign'] as const

/**
 * What `buildCardSkinCss` reads: every shared leaf, plus the explorer's own
 * five `loadMore*` knobs. PARTIAL on purpose — related-properties declares the
 * shared leaves only, and every read in the builder is already guarded, so an
 * absent key behaves exactly like a neutral one. Type-only: no widget parses
 * with a ZodObject over this shape, so none is built.
 */
export type CardSkinOptions = Partial<z.infer<z.ZodObject<typeof cardSkinFields>>> & {
  loadMoreBg?: string
  loadMoreText?: string
  loadMoreHover?: string
  loadMoreRadius?: number
  loadMoreAlign?: 'center' | 'left' | 'right'
}
